import React, {
  ReactNode, useContext, useEffect,
} from 'react';
import { DispatchContext, StateContext } from '../../../store';
import { UserActionType } from '../../../store/modules/user/user.enum';
import {
  ACCESS_TOKEN_EXPIRY_KEY,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_ID,
} from '../../../store/modules/user/user.constants';

import { useLocalStorage } from '../local-storage-provider/local-storage.context';
import { IAuthType } from '../../../store/modules/user/i-auth';

interface IProps {
  children: ReactNode;
}

interface IAuthTypeResponse {
  auth_type: string;
  authenticated: boolean;
  user_id: number | null;
  username: string | null;
  tenant?: string;
  accessToken?: string;
  accessTokenExpiry?: number;
  refreshToken?: string;
}

const AuthTypeInitializer = ({ children }: IProps): JSX.Element => {
  const dispatch = useContext(DispatchContext);
  const state = useContext(StateContext);
  const { authType } = state;
  const { loading, loaded } = authType;
  const localStorageService = useLocalStorage();

  useEffect(() => {
    const loadAuthType = async (): Promise<void> => {
      dispatch({ type: UserActionType.GET_AUTH_TYPE_REQUEST });

      try {
        const apiHost = process.env.REACT_APP_API_HOST || '';
        const response = await fetch(`${apiHost}/user-manager/api/auth/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch auth type: ${response.status}`);
        }

        const data: IAuthTypeResponse = await response.json();

        const authTypePayload: IAuthType = {
          auth_type: data.auth_type,
          authenticated: data.authenticated,
          user_id: data.user_id ?? null,
          username: data.username ?? null,
        };

        if ((data.auth_type === 'OAUTH2' || data.auth_type === 'OPENID') && data.authenticated && authTypePayload.user_id !== null) {
          if (data.accessToken) {
            localStorageService.set(ACCESS_TOKEN_KEY, String(data.accessToken));
          }
          if (data.accessTokenExpiry) {
            localStorageService.set(ACCESS_TOKEN_EXPIRY_KEY, String(data.accessTokenExpiry));
          }
          if (data.refreshToken) {
            localStorageService.set(REFRESH_TOKEN_KEY, String(data.refreshToken));
          }
          if (authTypePayload.user_id != null) {
            localStorageService.set(USER_ID, authTypePayload.user_id);
          }
        }
        dispatch({
          type: UserActionType.GET_AUTH_TYPE_SUCCESS,
          payload: authTypePayload,
        });
      } catch (error) {
        dispatch({
          type: UserActionType.GET_AUTH_TYPE_FAIL,
          payload: error as Error,
        });
      }
    };

    loadAuthType();
  }, [dispatch]);

  if (loading || !loaded) {
    return <div>Loading...</div>;
  }

  return <>{children}</>;
};

AuthTypeInitializer.displayName = 'AuthTypeInitializer';

export default AuthTypeInitializer;
